import { useState, useEffect } from "react"
import { BookOpen, Image as ImageIcon, DollarSign, Award, Users, Clock, FileText, Save, X, Plus, Trash2, Tag } from "lucide-react"
import { Button } from "../ui/button"
import ImageUpload from "../ImageUpload"
import { useCategories } from "../../hooks/useCategories"

export default function CourseCreationForm({
  onSave,
  onCancel,
  initialData = null
}) {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    shortDescription: '',
    price: '',
    originalPrice: '',
    currency: 'XOF',
    duration: 0,
    level: 'BEGINNER',
    language: 'Français',
    status: 'DRAFT',
    isPublished: false,
    isFeatured: false,
    isFree: false,
    maxStudents: 100,
    certificate: false,
    certificateTemplate: '',
    accessDurationDays: 90,
    requirements: [],
    objectives: [],
    categoryId: '',
    tagIds: [],
    thumbnail: null,
    thumbnailFile: null
  })

  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [newRequirement, setNewRequirement] = useState('')
  const [newObjective, setNewObjective] = useState('')
  
  const { categories } = useCategories()

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        slug: initialData.slug || '',
        description: initialData.description || '',
        shortDescription: initialData.shortDescription || '',
        price: initialData.price || '',
        originalPrice: initialData.originalPrice || '',
        currency: initialData.currency || 'XOF',
        duration: initialData.duration || 0,
        level: initialData.level || 'BEGINNER',
        language: initialData.language || 'Français',
        status: initialData.status || 'DRAFT',
        isPublished: initialData.isPublished || false,
        isFeatured: initialData.isFeatured || false,
        isFree: initialData.isFree || false,
        maxStudents: initialData.maxStudents || 100,
        certificate: initialData.certificate || false,
        certificateTemplate: initialData.certificateTemplate || '',
        accessDurationDays: initialData.accessDurationDays || 90,
        requirements: initialData.requirements || [],
        objectives: initialData.objectives || [],
        categoryId: initialData.categoryId || '',
        tagIds: initialData.tagIds || [],
        thumbnail: initialData.thumbnail || null,
        thumbnailFile: initialData.thumbnailFile || null
      })
    }
  }, [initialData])

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !formData.slug) {
      const slug = formData.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      setFormData(prev => ({ ...prev, slug }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est obligatoire'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'La description est obligatoire'
    }
    if (!formData.isFree && (!formData.price || parseFloat(formData.price) < 0)) {
      newErrors.price = 'Le prix est obligatoire et doit être positif'
    }
    if (!formData.categoryId) {
      newErrors.categoryId = 'La catégorie est obligatoire'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddItem = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }))
      setter('')
    }
  }

  const handleRemoveItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    try {
      // Convert duration from minutes to seconds if needed
      const courseData = {
        ...formData,
        duration: formData.duration ? formData.duration * 60 : 0, // Convert minutes to seconds
        price: formData.isFree ? 0 : (parseFloat(formData.price) || 0),
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
        isPublished: formData.status === 'PUBLISHED',
        // Ensure arrays are arrays
        requirements: Array.isArray(formData.requirements) ? formData.requirements : [],
        objectives: Array.isArray(formData.objectives) ? formData.objectives : [],
        keywords: Array.isArray(formData.keywords) ? formData.keywords : [],
        tagIds: Array.isArray(formData.tagIds) ? formData.tagIds : []
      }
      await onSave(courseData)
    } catch (error) {
      console.error('Error saving course:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {initialData ? 'Modifier le cours' : 'Nouveau Cours'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {initialData ? 'Modifiez les informations de base du cours' : 'Définissez les informations de base de votre cours'}
              </p>
            </div>
          </div>
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Informations de base
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Formation React Avancée"
                    required
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                      errors.slug ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="formation-react-avancee"
                  />
                  {errors.slug && (
                    <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Généré automatiquement à partir du titre</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description courte
                  </label>
                  <input
                    type="text"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Une description courte qui apparaîtra dans les listes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description complète <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Description détaillée du cours"
                    required
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Category */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Catégorie
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                    errors.categoryId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.displayName || category.translatedName || category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
                )}
              </div>
            </div>

            {/* Media */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Médias
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Miniature
                  </label>
                  <ImageUpload
                    className="w-full max-w-md mx-auto"
                    currentImage={formData.thumbnail}
                    onImageSelect={(file) => {
                      if (file instanceof File) {
                        setFormData(prev => ({ ...prev, thumbnailFile: file, thumbnail: null }))
                      } else {
                        setFormData(prev => ({ ...prev, thumbnailFile: null, thumbnail: file }))
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Tarification
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isFree"
                    checked={formData.isFree}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Cours gratuit</label>
                </div>
                {!formData.isFree && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix ({formData.currency}) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                        errors.price ? 'border-red-500' : 'border-gray-300'
                      }`}
                      min="0"
                      placeholder="0"
                      required
                    />
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix original ({formData.currency})
                  </label>
                  <input
                    type="number"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Devise
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="XOF">XOF</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Paramètres
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="BEGINNER">Débutant</option>
                    <option value="INTERMEDIATE">Intermédiaire</option>
                    <option value="ADVANCED">Avancé</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Langue
                  </label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="Français">Français</option>
                    <option value="English">English</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Durée (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Étudiants maximum
                  </label>
                  <input
                    type="number"
                    name="maxStudents"
                    value={formData.maxStudents}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="1"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée d'accès (jours)
                  </label>
                  <input
                    type="number"
                    name="accessDurationDays"
                    value={formData.accessDurationDays}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="1"
                    placeholder="90"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="DRAFT">Brouillon</option>
                    <option value="PUBLISHED">Publié</option>
                    <option value="ARCHIVED">Archivé</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Cours en vedette</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="certificate"
                    checked={formData.certificate}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Certificat disponible</label>
                </div>
                {formData.certificate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modèle de certificat
                    </label>
                    <input
                      type="text"
                      name="certificateTemplate"
                      value={formData.certificateTemplate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                      placeholder="certificate-template-v1"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Prérequis</h3>
              <div className="space-y-2">
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                      {req}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem('requirements', index)}
                      className="h-8 w-8 p-0 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItem('requirements', newRequirement, setNewRequirement)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Ajouter un prérequis"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleAddItem('requirements', newRequirement, setNewRequirement)}
                    className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Objectives */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Objectifs</h3>
              <div className="space-y-2">
                {formData.objectives.map((obj, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                      {obj}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem('objectives', index)}
                      className="h-8 w-8 p-0 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newObjective}
                    onChange={(e) => setNewObjective(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItem('objectives', newObjective, setNewObjective)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    placeholder="Ajouter un objectif"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleAddItem('objectives', newObjective, setNewObjective)}
                    className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving ? (
            <>
              <Save className="w-4 h-4 mr-2 animate-spin" />
              Création...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {initialData ? 'Modifier' : 'Créer le cours'}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

